/**
 * Cold-start detection.
 *
 * During the first 14 days, suppress the numeric score and show
 * completion counts instead (e.g., "3 for 5 this week").
 */

import { addDays, compareDates, daysBetween } from "./day-boundary";
import { getWeekBounds } from "./schedule";

import type { CompletionRecord } from "./types";

export const COLD_START_DAYS = 14;

/**
 * Check whether a habit is still in the cold-start period.
 * Returns true if the habit was created less than 14 days ago.
 */
export function isColdStart(habitCreatedDate: string, currentDate: string): boolean {
  return daysBetween(habitCreatedDate, currentDate) < COLD_START_DAYS;
}

/**
 * Get cold-start display data: completed/total counts for the current week.
 * Used in the UI when the habit is too new for a meaningful score.
 */
export function getColdStartDisplay(
  completions: CompletionRecord[],
  dueDates: string[],
  currentDate: string,
  firstDayOfWeek: number,
): { completedThisWeek: number; totalThisWeek: number } {
  const week = getWeekBounds(currentDate, firstDayOfWeek);
  const dueDateSet = new Set(dueDates);
  const completionSet = buildCompletionDateSet(completions);

  let completedThisWeek = 0;
  let totalThisWeek = 0;
  let current = week.start;

  while (compareDates(current, currentDate) <= 0 && compareDates(current, week.end) <= 0) {
    if (dueDateSet.has(current)) {
      totalThisWeek++;
      if (completionSet.has(current)) {
        completedThisWeek++;
      }
    }
    current = addDays(current, 1);
  }

  return { completedThisWeek, totalThisWeek };
}

function buildCompletionDateSet(completions: CompletionRecord[]): Set<string> {
  const set = new Set<string>();
  for (const c of completions) {
    if (c.type === "done" || c.type === "backfill") {
      set.add(c.effective_date);
    }
  }
  return set;
}
